import React, { useRef, useState } from 'react';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import { Button } from '../../components/ui';
import { SpinnerIcon, UploadCloudIcon, XIcon } from '../../components/ui/Icons';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { importEFormFromFile, type EFormImportResult } from '../../services/mutations';

const ACCEPT = '.pdf,.doc,.docx,image/png,image/jpeg,image/webp';
const MAX_BYTES = 15 * 1024 * 1024;

function errMsg(e: unknown): string {
  const msg = (e as { response?: { data?: { message?: unknown } }; message?: string })?.response?.data
    ?.message;
  if (Array.isArray(msg)) return msg.filter((m) => typeof m === 'string').join(', ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return (e as { message?: string })?.message || 'Could not import this file.';
}

type ImportFromFileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** When set, replaces the current draft instead of creating a new form. */
  definitionId?: number;
  onImported: (result: EFormImportResult) => void;
};

const ImportFromFileModal: React.FC<ImportFromFileModalProps> = ({
  isOpen,
  onClose,
  definitionId,
  onImported,
}) => {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setError(null);
    setDragActive(false);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const pick = (next: File | null) => {
    setError(null);
    if (!next) {
      setFile(null);
      return;
    }
    if (next.size > MAX_BYTES) {
      setError('File is larger than 15 MB.');
      setFile(null);
      return;
    }
    setFile(next);
  };

  const handleImport = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await importEFormFromFile(file, definitionId);
      reset();
      onImported(result);
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  const replacing = definitionId != null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader>
        <ModalTitle>{replacing ? 'Replace draft from file' : 'Import from file'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <p className={cn('mb-3 text-sm', colors.textSecondary)}>
          Upload a PDF, Word (.doc or .docx), or image of a form. Atlas reads the document
          {busy ? ' and is building a draft' : ''} — you can edit it in the builder before publishing.
          The original file is stored as the export template, so you can later fill a submission back
          into this PDF or Word file.
          {replacing
            ? ' This replaces the current draft and template only; published submissions stay as they are.'
            : ''}
        </p>
        <p className={cn('mb-3 text-xs', colors.textMuted)}>
          For filled export: use a PDF with fillable fields, or a Word (.docx) file with{' '}
          <span className="font-mono">{'{{field_key}}'}</span> placeholders that match the form
          fields. Scanned PDFs, images, and .doc files are kept and can be downloaded, but cannot be
          filled automatically.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          disabled={busy}
          onChange={(e) => pick(e.target.files?.[0] ?? null)}
        />

        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!busy && e.dataTransfer.types.includes('Files')) setDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (busy) return;
            pick(e.dataTransfer.files?.[0] ?? null);
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center transition',
            colors.border,
            dragActive ? 'bg-teal-50 dark:bg-teal-950/30' : colors.surfaceHover,
            busy && 'opacity-70',
          )}
        >
          {busy ? (
            <>
              <SpinnerIcon className={cn('h-8 w-8', colors.textMuted)} />
              <p className={cn('mt-3 text-sm font-medium', colors.text)}>Reading document…</p>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>This can take a minute for scanned PDFs.</p>
            </>
          ) : file ? (
            <>
              <FileIcon fileName={file.name} size="md" />
              <p className={cn('mt-3 text-sm font-medium', colors.text)}>{file.name}</p>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>{formatFileSize(file.size)}</p>
              <span className={cn('mt-2 text-xs', colors.textMuted)}>Click or drop to choose another file</span>
            </>
          ) : (
            <>
              <UploadCloudIcon className={cn('h-8 w-8', colors.textMuted)} />
              <p className={cn('mt-3 text-sm font-medium', colors.text)}>Drop PDF or Word file here</p>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>PDF, DOC, DOCX, PNG, or JPEG · max 15 MB</p>
            </>
          )}
        </button>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="ghost" onClick={handleClose} disabled={busy}>
          <XIcon className="w-4 h-4" />
          Cancel
        </Button>
        <Button onClick={handleImport} disabled={!file || busy}>
          {busy ? 'Importing…' : replacing ? 'Replace draft' : 'Create draft'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ImportFromFileModal;
